import React, { Suspense, useEffect, useState } from 'react';

const pending = new Set();
// Suspense boundaries rendered on the client that haven't committed yet. On
// startup that means boundaries that are still dehydrated (server HTML not
// hydrated yet).
const uncommitted = new Set();
let onAllCommitted = [];

const committed = (token) => {
  uncommitted.delete(token);
  if (uncommitted.size) return;
  onAllCommitted.forEach((resolve) => resolve());
  onAllCommitted = [];
};

function CommitMarker({ token }) {
  // Runs when the boundary around it commits, i.e. after it hydrated.
  useEffect(() => committed(token), [token]);
  return null;
}

/**
 * Same as `Suspense`, but lets `whenLazyComponentsLoaded` wait until the
 * boundary has hydrated, not only until its lazy chunk has loaded.
 *
 * @param {object} props - Suspense props.
 * @param {*} props.children - The boundary's content.
 * @returns {JSX.Element}
 */
export function TrackedSuspense({ children, ...props }) {
  const [token] = useState(() => {
    const newToken = {};
    if (typeof window !== 'undefined') uncommitted.add(newToken);
    return newToken;
  });
  // Unmounted before committing (e.g. its error boundary caught an error).
  useEffect(() => () => committed(token), [token]);

  return (
    <Suspense {...props}>
      {children}
      <CommitMarker token={token} />
    </Suspense>
  );
}

/**
 * Same as `React.lazy`, but keeps track of imports that are still loading so
 * that `whenLazyComponentsLoaded` can wait for them.
 *
 * @param {() => Promise<{ default: import('react').ComponentType<any> }>} load
 * @returns {import('react').LazyExoticComponent<any>}
 */
export function trackedLazy(load) {
  return React.lazy(() => {
    const promise = load();
    pending.add(promise);
    promise.finally(() => pending.delete(promise)).catch(() => {});
    return promise;
  });
}

/**
 * Resolves once no lazy import is loading anymore and every Suspense boundary
 * rendered through TrackedSuspense has hydrated (at most MAX_WAIT_MS).
 *
 * While a lazy component's chunk is loading, its server-rendered Suspense
 * boundary is still dehydrated. A store update reaching it at that point
 * makes React throw away the server HTML and client-render it instead
 * (React error #421). Wait for this before dispatching updates on startup.
 *
 * @returns {Promise<void>}
 */
const MAX_WAIT_MS = 10000;

export async function whenLazyComponentsLoaded() {
  // React only starts hydrating Suspense boundaries after the root has
  // committed, so on startup nothing is pending yet. The load event waits for
  // the chunks requested until then.
  if (document.readyState !== 'complete') {
    await new Promise((resolve) =>
      window.addEventListener('load', resolve, { once: true })
    );
  }
  await new Promise((resolve) => setTimeout(resolve));

  const allLoadedAndHydrated = (async () => {
    while (pending.size || uncommitted.size) {
      await Promise.allSettled(pending);
      // A loaded chunk isn't hydrated yet: React hydrates the boundary in
      // later tasks, and on a slow phone that takes a while. Wait for every
      // boundary to commit; hydrating them may start nested lazy imports.
      if (uncommitted.size) {
        await new Promise((resolve) => onAllCommitted.push(resolve));
      }
      await new Promise((resolve) => setTimeout(resolve));
    }
  })();
  // Never hold startup updates back for long if a boundary gets stuck.
  await Promise.race([
    allLoadedAndHydrated,
    new Promise((resolve) => setTimeout(resolve, MAX_WAIT_MS)),
  ]);
}
